import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const FeedbackHistory = () => {
  const { user, currentTenant } = useAuth();
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadFeedbackHistory();
  }, [user, currentTenant]);

  const loadFeedbackHistory = async () => {
    if (!user?.id || !currentTenant?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('workout_feedback')
        ?.select(`
          *,
          workout_sessions(workout_name, total_duration_seconds, actual_calories)
        `)
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setFeedbackHistory(data || []);
    } catch (error) {
      console.error('Error loading feedback history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      too_easy: { text: 'Too Easy', color: 'var(--color-success)' },
      just_right: { text: 'Just Right', color: 'var(--color-primary)' },
      too_hard: { text: 'Too Hard', color: 'var(--color-error)' }
    };
    return labels?.[difficulty] || { text: 'N/A', color: 'var(--color-muted-foreground)' };
  };

  const getEnergyLabel = (energy) => {
    const labels = {
      low: { text: 'Low Energy', emoji: '😴' },
      moderate: { text: 'Moderate', emoji: '😊' },
      high: { text: 'High Energy', emoji: '🔥' }
    };
    return labels?.[energy] || { text: 'N/A', emoji: '❓' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Loader" size={32} className="text-primary animate-spin mx-auto mb-2" />
          <p className="text-muted-foreground">Loading feedback history...</p>
        </div>
      </div>
    );
  }

  if (feedbackHistory?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Feedback History</h3>
        <p className="text-muted-foreground">Your submitted workout feedback will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-foreground mb-4">Feedback History</h2>
      {feedbackHistory?.map((feedback) => {
        const isExpanded = expandedId === feedback?.id;
        const difficultyConfig = getDifficultyLabel(feedback?.difficulty_level);
        const energyConfig = getEnergyLabel(feedback?.energy_level);

        return (
          <div key={feedback?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : feedback?.id)}
              className="w-full p-5 flex items-center justify-between hover:bg-muted/50 transition-colors text-left"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">
                    {feedback?.workout_sessions?.workout_name || 'Workout'}
                  </h3>
                  <div className="flex items-center gap-1">
                    {[...Array(5)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={16}
                        className={`${
                          i < feedback?.rating
                            ? 'fill-warning text-warning' :'text-muted-foreground'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{formatDate(feedback?.created_at)}</p>
              </div>
              <Icon
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                size={20}
                className="text-muted-foreground flex-shrink-0"
              />
            </button>
            {isExpanded && (
              <div className="px-5 pb-5 border-t border-border pt-4 space-y-4">
                {/* Workout Details */}
                {feedback?.workout_sessions && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Duration</p>
                      <p className="text-sm font-semibold text-foreground">
                        {feedback?.workout_sessions?.total_duration_seconds} seconds
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Calories</p>
                      <p className="text-sm font-semibold text-foreground">
                        {feedback?.workout_sessions?.actual_calories} kcal
                      </p>
                    </div>
                  </div>
                )}

                {/* Feedback Details */}
                <div className="grid grid-cols-2 gap-3">
                  {feedback?.difficulty_level && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Difficulty</p>
                      <p className="text-sm font-semibold" style={{ color: difficultyConfig?.color }}>
                        {difficultyConfig?.text}
                      </p>
                    </div>
                  )}
                  {feedback?.energy_level && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Energy Level</p>
                      <p className="text-sm font-semibold text-foreground">
                        {energyConfig?.emoji} {energyConfig?.text}
                      </p>
                    </div>
                  )}
                </div>

                {/* Pain Points */}
                {feedback?.pain_points && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-error mb-1">Pain/Discomfort</p>
                        <p className="text-sm text-foreground">{feedback?.pain_points}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comments */}
                {feedback?.comments && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">Comments</p>
                    <p className="text-sm text-foreground leading-relaxed">{feedback?.comments}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FeedbackHistory;